import { CompositeLayer } from "@deck.gl/core";
import {
  ScatterplotLayer,
  ScatterplotLayerProps,
  TextLayer,
  TextLayerProps,
} from "@deck.gl/layers";
import { useMemo } from "react";

import { COLORS } from "@/config/deckgl-conts";

import { MapProject } from "../types/map-project";
import { useTenantProjectList } from "../use-tenant-project-list";

type TextScatterplotLayerOwnProps = {
  getTextColor: TextLayerProps["getColor"];
};

type TextScatterplotLayerProps<DataT extends object> = Omit<
  ScatterplotLayerProps<DataT> & TextLayerProps<DataT> & TextScatterplotLayerOwnProps,
  "getColor"
>;

class TextScatterplotLayer<T extends object> extends CompositeLayer<
  Required<TextScatterplotLayerProps<T>>
> {
  static override layerName = "TextScatterplotLayer";

  renderLayers() {
    return [
      new ScatterplotLayer(
        this.getSubLayerProps({
          ...this.props,
          id: this.props.id + "-scatterplot",
          data: this.props.data,

          updateTriggers: this.props.updateTriggers,
        })
      ),
      new TextLayer(
        this.getSubLayerProps({
          // @ts-expect-error to make the default conistent with the scatterplot
          billboard: false,
          ...this.props,
          id: this.props.id + "-text",
          data: this.props.data,
          getColor: this.props.getTextColor,
          updateTriggers: this.props.updateTriggers,
        })
      ),
    ];
  }
}

type UseTenantProjectListMapLayersProps = Pick<
  ReturnType<typeof useTenantProjectList>,
  | "projectsOnMap"
  | "handleMapClick"
  | "selectedProjectIds"
  | "hoveredProjectId"
  | "setHoveredProjectId"
>;
export function useTenantProjectListMapLayers({
  projectsOnMap,
  handleMapClick,
  selectedProjectIds,
  hoveredProjectId,
  setHoveredProjectId,
}: UseTenantProjectListMapLayersProps) {
  // Base props for all project markers
  const baseScatterplotProps: TextScatterplotLayerProps<MapProject> = useMemo(
    () => ({
      // All
      id: "scatterplot-text-layer-",
      data: [],
      pickable: true,
      stroked: true,
      filled: true,
      getPosition: (d) => d.location.coordinates as unknown as [number, number],

      // Scatterplot
      radiusUnits: "pixels",
      lineWidthUnits: "pixels",
      getRadius: 15,
      getFillColor: COLORS.ANALYTIC,
      getLineColor: COLORS.WHITE,
      getLineWidth: 2,

      // Text
      getText: (d) => d.name[0],
      getAlignmentBaseline: "center",
      getTextColor: COLORS.WHITE,
      getSize: 19,
      getTextAnchor: "middle",
      fontWeight: 500,

      // Listeners
      onClick: (e) => {
        handleMapClick(e.object.location.coordinates);
        return true;
      },
      onHover: (e) => {
        // If object is undefined it means the users cursor has left the marker, so we set the hovered project to null
        const isHovering = "object" in e && e.object;
        if (isHovering) {
          const object: MapProject = e.object;
          setHoveredProjectId(object.id);
        } else {
          setHoveredProjectId(null);
        }
      },
    }),
    [handleMapClick, setHoveredProjectId]
  );

  const baseLayers = useMemo(() => {
    // Performance optimization:
    // Below 200 projects: create individual layers for each project (prevents z-fighting (markers partially overlapping each other))
    // Above 200 projects: use single layer with all data (prevents lag)
    if (projectsOnMap.length < 200)
      return projectsOnMap.map(
        (project, idx) =>
          new TextScatterplotLayer<MapProject>({
            ...baseScatterplotProps,
            id: "scatterplot-text-layer-" + idx,
            data: [project],
          })
      );
    return [
      new TextScatterplotLayer<MapProject>({
        ...baseScatterplotProps,
        id: "scatterplot-text-layer-all",
        data: projectsOnMap,
      }),
    ];
  }, [baseScatterplotProps, projectsOnMap]);

  const activeLayers = useMemo(() => {
    const activeProjects = projectsOnMap.filter(
      (project) => selectedProjectIds.includes(project.id) || project.id === hoveredProjectId
    );

    // Render active project on top as a separate layer to prevent z-fighting (markers partially overlapping each other) and to make it more visible
    return activeProjects.map((project, idx) => {
      const isSelected = selectedProjectIds.includes(project.id);

      return new TextScatterplotLayer<MapProject>({
        ...baseScatterplotProps,
        id: "scatterplot-text-layer-active-" + idx,
        data: [project],
        // Active project styling: larger size and highlighted border
        getLineColor: COLORS.CLEAR_CREEK,
        ...(isSelected ? { getRadius: 17, getSize: 22 } : {}),
      });
    });
  }, [projectsOnMap, selectedProjectIds, hoveredProjectId, baseScatterplotProps]);

  return [...baseLayers, ...activeLayers];
}
